import React, { PureComponent } from 'react';
import { Flex } from 'app/components/common/Flex';
import { SettingsContentPanel } from 'app/components/common/SettingsContentPanel';
import { Button, Checkbox, Input, Select } from 'antd';
import {
    EnvironmentLayerUtils,
    MapLayer,
    RegulatedLayerDelayLevelEnum,
    RegulationOutlookPredictionEnum,
    WeatherMinLevel,
} from 'app/components/dashboard/network/map/Map';
import Utils from 'app/common/util/Utils';
import { CountType } from 'app/model/CORE';
import SelectEnum from 'app/common/input/SelectEnum';
import { AlertType } from 'app/components/map/util/MapUtils';
import { ConvectionSource } from 'app/model/SOL49';
import AppContext from 'app/common/context/AppContext';
import { DefaultOptionType } from 'rc-select/lib/Select';

export class MapLayerSettingsPopupTitle extends PureComponent {
    render() {
        return (
            <Flex direction="vertical">
                <div style={{ fontWeight: 'bold' }}>LAYER SETTINGS</div>
            </Flex>
        );
    }
}

export class MapLayerSettingsPopupContent extends SettingsContentPanel<MapLayer> {
    render() {
        return (
            <Flex direction="vertical" className="settings-content" style={{ width: '20em', padding: '1em' }}>
                <Flex className="label">
                    <Checkbox
                        checked={this.state.showLabel}
                        onChange={value => this.setState({ showLabel: value.target.checked })}>
                        Show Labels
                    </Checkbox>
                </Flex>
                <>{this.renderCustomProperties()}</>
                <Flex style={{ justifyContent: 'space-around' }}>
                    <Button onClick={this.handleApplyButton}>Apply</Button>
                    <Button onClick={this.handleCloseButton}>Close</Button>
                </Flex>
            </Flex>
        );
    }

    handleApplyButton = event => {
        event.stopPropagation();
        this.handleApply();
    };
    handleCloseButton = event => {
        event.stopPropagation();
        this.handleClose();
    };

    renderCustomProperties() {
        if (Utils.isExists(this.state.customProperties) && Object.keys(this.state.customProperties)?.length > 0) {
            return Object.keys(this.state.customProperties).map(propKey => {
                if (
                    propKey === EnvironmentLayerUtils.REGULATION_OUTLOOK_MIN_PREDICTION &&
                    !this.state.customProperties[EnvironmentLayerUtils.REGULATION_OUTLOOK]
                )
                    return null; // Don't display prediction filter if reg outlook are not active
                else return this.renderCustomProperty(propKey, this.state.customProperties[propKey]);
            });
        }
    }

    renderCustomProperty(
        key: string,
        value:
            | string
            | boolean
            | number
            | WeatherMinLevel
            | RegulatedLayerDelayLevelEnum
            | RegulationOutlookPredictionEnum
            | AlertType,
    ) {
        let propInput;

        if (value instanceof WeatherMinLevel) {
            const options: DefaultOptionType[] = [
                { value: 1, label: 'Low' },
                { value: 2, label: 'Medium' },
                { value: 3, label: 'High' },
            ];
            if (AppContext.getConvectionSource() === ConvectionSource.METSAFE) {
                options.push({ value: 4, label: 'Critical' });
            }
            if (AppContext.getConvectionSource() === ConvectionSource.DWD) {
                options.push({ value: 4, label: 'Very High' });
                options.push({ value: 5, label: 'Critical' });
            }
            propInput = (
                <>
                    <span style={{ lineHeight: '2em' }}>Convection Risk Level</span>
                    <Select
                        options={options}
                        value={value.getLevel()}
                        onChange={onChangeValue =>
                            this.handleCustomPropertyChange(key, new WeatherMinLevel(onChangeValue))
                        }
                        style={{ paddingLeft: '0.5em', width: '8em' }}
                        // Force render  of dropdown inside the popover content
                        // Fix popover hiding on click
                        getPopupContainer={trigger => trigger.parentNode}
                    />
                </>
            );
        } else if (Object.values(CountType).includes(value as unknown as CountType)) {
            propInput = (
                <SelectEnum
                    enumType={CountType}
                    value={value}
                    onChange={onChangeValue => this.handleCustomPropertyChange(key, onChangeValue as CountType)}
                    style={{ width: '9em' }}
                    // Force render  of dropdown inside the popover content
                    // Fix popover hiding on click
                    getPopupContainer={trigger => trigger.parentNode}
                />
            );
        } else if (key === EnvironmentLayerUtils.REGULATED_LAYER_MIN_DELAY) {
            propInput = (
                <>
                    <span style={{ lineHeight: '2em' }}>Min Delay:</span>
                    <SelectEnum
                        enumType={RegulatedLayerDelayLevelEnum}
                        value={value}
                        onChange={onChangeValue =>
                            this.handleCustomPropertyChange(key, onChangeValue as RegulatedLayerDelayLevelEnum)
                        }
                        style={{ paddingLeft: '0.5em', width: '9em' }}
                        // Force render  of dropdown inside the popover content
                        // Fix popover hiding on click
                        getPopupContainer={trigger => trigger.parentNode}
                    />
                </>
            );
        } else if (key === EnvironmentLayerUtils.REGULATION_OUTLOOK_MIN_PREDICTION) {
            propInput = (
                <div style={{ paddingLeft: '1em' }}>
                    <span style={{ lineHeight: '2em' }}>Min Prediction:</span>
                    <SelectEnum
                        enumType={RegulationOutlookPredictionEnum}
                        displayName={(v: RegulationOutlookPredictionEnum) => {
                            switch (v) {
                                case RegulationOutlookPredictionEnum.ALL:
                                    return 'ALL';
                                case RegulationOutlookPredictionEnum.PRED_30:
                                    return '30%';
                                case RegulationOutlookPredictionEnum.PRED_50:
                                    return '50%';
                                case RegulationOutlookPredictionEnum.PRED_80:
                                    return '80%';
                            }
                        }}
                        value={value}
                        onChange={onChangeValue =>
                            this.handleCustomPropertyChange(key, onChangeValue as RegulationOutlookPredictionEnum)
                        }
                        style={{ paddingLeft: '0.5em', width: '6em' }}
                        // Force render  of dropdown inside the popover content
                        // Fix popover hiding on click
                        getPopupContainer={trigger => trigger.parentNode}
                    />
                </div>
            );
        } else if (Object.values(AlertType).includes(value as unknown as AlertType)) {
            propInput = (
                <SelectEnum
                    enumType={AlertType}
                    displayName={(v: AlertType) => {
                        switch (v) {
                            case AlertType.NEUTRAL:
                                return 'ALL';
                            case AlertType.ORANGE:
                                return 'HIGH';
                            case AlertType.RED:
                                return 'OVERLOAD';
                        }
                    }}
                    value={value}
                    onChange={onChangeValue => this.handleCustomPropertyChange(key, onChangeValue as AlertType)}
                    style={{ width: '9em' }}
                    // Force render  of dropdown inside the popover content
                    // Fix popover hiding on click
                    getPopupContainer={trigger => trigger.parentNode}
                />
            );
        } else {
            switch (typeof value) {
                case 'boolean':
                    propInput = (
                        <Checkbox
                            checked={value}
                            onChange={event => this.handleCustomPropertyChange(key, event.target.checked)}>
                            {key}
                        </Checkbox>
                    );
                    break;
                case 'number':
                    propInput = (
                        <>
                            <div className="label" style={{ marginRight: '1em' }}>
                                {key}
                            </div>
                            <Input
                                type="number"
                                value={value}
                                onChange={event => this.handleCustomPropertyChange(key, event.target.value)}
                            />
                        </>
                    );
                    break;
                case 'string':
                    propInput = (
                        <>
                            <div className="label" style={{ marginRight: '1em' }}>
                                {key}
                            </div>
                            <Input
                                type="text"
                                value={value}
                                onChange={event => this.handleCustomPropertyChange(key, event.target.value)}
                            />
                        </>
                    );
                    break;
                default:
                    return null;
            }
        }
        return (
            <Flex key={`flex-label-${key}`} className="label">
                {propInput}
            </Flex>
        );
    }

    handleCustomPropertyChange = (
        key: string,
        value:
            | string
            | boolean
            | number
            | WeatherMinLevel
            | RegulatedLayerDelayLevelEnum
            | RegulationOutlookPredictionEnum
            | AlertType,
    ) => {
        this.setState(prevState => ({
            customProperties: Object.assign({}, prevState.customProperties, { [key]: value }),
        }));
    };
}
